import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import ClearFiltersButton from "../components/ClearFiltersButton";
import { searchIcon } from "../../../assets";
import {
  getStatusBackground,
  getStatusDotColor,
  getStatusTextColor,
} from "../../../utils/statusConstants";
import ReportsService from "../../../services/ReportsService";
import { componentKey } from "../ReportsSlice";
import { getInventoryReport } from "../ReportsSaga";
import {
  CSV_HEADERS_INVENTORY,
  INVENTORY_TYPE_OPTIONS,
  ROWS_PER_PAGE_OPTIONS,
  STOCK_STATUS_OPTIONS,
  normalizeStockStatus,
} from "../ReportConstants";
import DownloadCsvButton from "../components/DownloadCsvButton";

type InventoryReportTabProps = { facilityId: string };

const InventoryReportTab = ({ facilityId }: InventoryReportTabProps) => {
  const dispatch = useDispatch();
  const { inventoryReport } = useSelector(
    (state: Record<string, any>) => state[componentKey] as Record<string, any>
  );

  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [searchedValue, setSearchedValue] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const rows: Array<Record<string, any>> = inventoryReport?.data || [];

  useEffect(() => {
    dispatch(
      getInventoryReport({
        pageNumber,
        limit: rowPerPage,
        facilityId: facilityId || undefined,
        searchValue: searchedValue?.trim() || undefined,
        type: type || undefined,
        status: status || undefined,
      })
    );
  }, [facilityId, pageNumber, rowPerPage, searchedValue, type, status]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);
    const value = e.target.value;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setSearchedValue(value?.trim());
    }, 500);
  };

  const isAnyFilterApplied = Boolean(searchedValue || type || status);

  const handleClearFilters = () => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    setSearchedValue("");
    setType("");
    setStatus("");
    setPageNumber(1);
  };

  const columns = [
    {
      field: "itemName",
      label: <div>Item Name</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.itemName || "-"}</p>
      ),
    },
    {
      field: "itemCode",
      label: <div>Item Code</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.itemCode || "-"}</p>
      ),
    },
    {
      field: "skuCode",
      label: <div>SKU Code</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.skuCode || "-"}</p>
      ),
    },
    {
      field: "type",
      label: <div>Type</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.type || "-"}</p>
      ),
    },
    {
      field: "unitOfMeasure",
      label: <div>Unit</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {row?.unitOfMeasure || "-"}
        </p>
      ),
    },
    {
      field: "totalStock",
      label: <div>Current Stock</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-weight-500">
          {row?.totalStock ?? 0}
        </p>
      ),
    },
    {
      field: "locations",
      label: <div>Stock By Location</div>,
      renderLogic: (row: Record<string, any>) => {
        const locations: Array<Record<string, any>> = row?.locations || [];
        if (!locations.length)
          return <span className="text-neutral-90 font-normal">-</span>;
        return (
          <div className="flex flex-col gap-1" style={{ minWidth: "220px" }}>
            {locations.map((loc, idx) => {
              const stockStatus = normalizeStockStatus(loc?.status);
              return (
                <div
                  key={`${loc?.facilityId}-${idx}`}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="text-neutral-90 font-normal">
                    {loc?.facilityName || "-"}
                  </span>
                  <span
                    className="flex items-center gap-1"
                    style={{
                      color: getStatusTextColor(stockStatus),
                      background: getStatusBackground(stockStatus),
                      borderRadius: "46px",
                      padding: "2px 10px",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span
                      className="w-2 h-2"
                      style={{
                        background: getStatusDotColor(stockStatus),
                        borderRadius: "50%",
                      }}
                    />
                    {loc?.currentStock ?? 0}
                  </span>
                </div>
              );
            })}
          </div>
        );
      },
    },
  ];

  const fetchCsvRows = async () => {
    const response = await ReportsService.getInventoryReport({
      limit: "All",
      facilityId: facilityId || undefined,
      searchValue: searchedValue?.trim() || undefined,
      type: type || undefined,
      status: status || undefined,
    });
    const items: Array<Record<string, any>> = response?.data?.data || [];
    return items.flatMap((item) =>
      (item?.locations || []).map((loc: Record<string, any>) => ({
        itemName: item?.itemName,
        itemCode: item?.itemCode,
        skuCode: item?.skuCode,
        type: item?.type,
        unitOfMeasure: item?.unitOfMeasure,
        threshold: item?.threshold,
        facilityName: loc?.facilityName,
        currentStock: loc?.currentStock,
        status: normalizeStockStatus(loc?.status),
        totalStock: item?.totalStock,
      }))
    );
  };

  return (
    <div className="mt-5">
      <div className="flex flex-wrap mb-4 gap-3 justify-end items-center">
        <SearchSelectDropdown
          freeSolo={true}
          searchedValue={searchedValue}
          onSearchValueChangeCb={handleSearchChange}
          size="small"
          label="Search by Item Name, Item Code, SKU Code"
          fullWidth={"300px"}
          startIcon={
            <img alt="search-icon" src={searchIcon} width={"18px"} height={"18px"} />
          }
        />
        <div style={{ minWidth: "170px" }}>
          <SelectDropdown
            label="Type"
            value={type}
            options={INVENTORY_TYPE_OPTIONS}
            onChangeCb={(e) => {
              setPageNumber(1);
              setType(e.target.value);
            }}
          />
        </div>
        <div style={{ minWidth: "170px" }}>
          <SelectDropdown
            label="Stock Status"
            value={status}
            options={STOCK_STATUS_OPTIONS}
            onChangeCb={(e) => {
              setPageNumber(1);
              setStatus(e.target.value);
            }}
          />
        </div>
        <ClearFiltersButton
          onClear={handleClearFilters}
          disabled={!isAnyFilterApplied}
        />
        <DownloadCsvButton
          headers={CSV_HEADERS_INVENTORY}
          filename="inventory-report.csv"
          fetchRows={fetchCsvRows}
          disabled={!rows.length}
        />
      </div>

      <Table
        columns={columns}
        rows={rows}
        noDataFoundMessage="No inventory found"
        paginationProps={{
          isPagination: true,
          page: pageNumber,
          totalPages: inventoryReport?.totalPages,
          totalCount: inventoryReport?.totalRecords,
          limit: rowPerPage,
          rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
          onPageChange: (page: number) => setPageNumber(page),
          handleChangeRowsPerPage: (limitValue: string) => {
            setRowPerPage(limitValue);
            setPageNumber(1);
          },
        }}
      />
    </div>
  );
};

export default InventoryReportTab;
